/**
 * The toast store: a module-level list of toasts, a reducer over it, and
 * `toast()` to raise one from anywhere, inside React or not.
 *
 * Copied from shadcn-ui. Module-level so a toast can be raised from a
 * service or a context with no component to hand; Toaster subscribes to draw
 * it. The limit and the removal delay are in tunables/ui.
 *
 * Used by: some twenty screens raise toasts with toast(); AuthContext clears
 * them on sign-out (clearToasts).
 *
 * @module use-toast
 */
import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/shared/components/ui/toast"
import { TOAST_DURATION_MS, TOAST_LIMIT, TOAST_REMOVE_DELAY_MS } from "@/config/tunables/ui"

/** A toast as the store holds it: Radix's props, an id, and its words. */
type ToasterToast = ToastProps & {
  /** Made by toast(); dismiss and update take it. */
  id: string
  /** The bold line. */
  title?: React.ReactNode
  /** The line under it. */
  description?: React.ReactNode
  /** A button in the toast (Undo), as ToastAction. */
  action?: ToastActionElement
}

/** The four things that can happen to the store. */
const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

/** The last id handed out. */
let count = 0

/** A fresh id for a toast, unique for the life of the page. */
function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

/** The action names, as types. */
type ActionType = typeof actionTypes

/** One change to the store. */
type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

/** The store: the toasts there are, newest first. */
interface State {
  /** At most TOAST_LIMIT, newest first. */
  toasts: ToasterToast[]
}

/** The removal waiting for each dismissed toast, so none is queued twice. */
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

/** Removes a dismissed toast from the store once its closing has played. */
const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY_MS)

  toastTimeouts.set(toastId, timeout)
}

/** The store's next state after an action. Dismissing also queues the removal. */
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

/**
 * Throws away every toast, shown or pending.
 *
 * The store is a module-level variable, so it survives any component
 * unmounting — including signing out. A toast raised moments before the
 * Toaster disappeared never gets dismissed, because dismissal happens when
 * the component reports it, so it sits there as open and appears in the next
 * session. Deleting an account did exactly that: the confirmation reappeared
 * after signing up again, telling a brand-new account it had been deleted.
 */
export function clearToasts(): void {
  toastTimeouts.forEach((timeout) => clearTimeout(timeout))
  toastTimeouts.clear()
  dispatch({ type: "REMOVE_TOAST" })
}

/** Every mounted useToast, told of each change. */
const listeners: Array<(state: State) => void> = []

/** The store's current state, outside React. */
let memoryState: State = { toasts: [] }

/** Applies an action and tells every listener. */
function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

/** What `toast()` takes: a toast without its id, which it makes. */
type Toast = Omit<ToasterToast, "id">

/**
 * Raises a toast. It closes itself after its duration (TOAST_DURATION_MS
 * unless given one; Infinity keeps it), even while Radix's own timer is held.
 *
 * @returns Its id, and ways to dismiss or change it.
 */
function toast({ ...props }: Toast): {
  id: string;
  dismiss: () => void;
  update: (props: ToasterToast) => void;
} {
  const id = genId()

  /** Changes this toast in place. */
  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  /** Closes this toast. */
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Closes itself after its time even when Radix's timer is held (over an open window, or focused).
  const duration = props.duration ?? TOAST_DURATION_MS
  if (Number.isFinite(duration)) window.setTimeout(dismiss, duration)

  return {
    id: id,
    dismiss,
    update,
  }
}

/** The toasts there are, kept current, and the ways to raise and dismiss one. */
function useToast(): {
  toasts: ToasterToast[];
  toast: typeof toast;
  dismiss: (toastId?: string) => void;
} {
  const [state, setState] = React.useState<State>(memoryState)

  // Subscribed once, on mount. It was keyed on the state (shadcn's copy), so
  // every toast unsubscribed and resubscribed every reader. setState is
  // stable, so there is nothing for it to follow.
  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast } 